// For the scenario grouping endpoint
import {InferenceParams} from '../services/stato-sessione.service';

export interface ScenarioGroupInfo {
  group_id: string;
  name: string;
  common_preconditions: string[];
  scenarios: string[];
  rationale: string;
}

export interface GroupingResponse {
  groups: ScenarioGroupInfo[];
  standalone_scenarios: string[];
}

// For the template generation endpoint
export interface TemplateGroupInfo {
  group_id: string;
  feature_name: string;
  background: string[];
  scenario_ids: string[];
  group_analysis?: string;
}

export interface AcceptanceCriterion {
  id: string;
  scenario_ref: string;
  name: string;
  tags: string[];
  test_data_elements: string[];
  given: string[];
  when: string[];
  then: string[];
}

export interface ScenarioResponse {
  scenario_groups: Array<{
    group_info: TemplateGroupInfo,
    acceptance_criteria: AcceptanceCriterion[]
  }>;
  token_usage: TokenCount;
}

// Ambiguity Interface
export interface Ambiguity {
  id: string;
  description: string;
  source: string;
  clarification_needed: string;
  impact: string;
  resolution?: string; // Field for resolution
  stato?: 'unanswered' | 'answered' | 'solved'; // Property for tracking state
  type?: string; // 'technical' | 'workflow' | 'domain' | 'business_rule'
  priority?: 'high' | 'medium' | 'low'; // Impact priority assigned by LLM

  // New property for multiple knowledge base items
  linkedKbItems?: LinkedKnowledgeBaseItem[]; // Array of linked knowledge base items

  // G2: Original agent proposal text for Caso A detection
  agent_proposed_text?: string;

  // True if the agent proposal came from an existing KB Item (not a document)
  agent_proposal_from_kb?: boolean;

  // Agent KB resolve result (from "Find bindings from KB" with search agent)
  agentResult?: AmbiguityAgentResult;
}

// New interface for linked KB items
export interface LinkedKnowledgeBaseItem {
  kbItemId: string;
  title: string;
  term: string;
  definition: string;
  examples?: string[];
  source_type?: string; // G6: e.g. "ambiguity_resolution" or "document"
}

// Agent KB resolve result per ambiguity
export interface AmbiguityAgentResult {
  ambiguity_id: string;
  proposed_text: string | null;
  confidence: 'high' | 'medium' | 'low' | null;
  sources: ResolutionSource[];
  debug: any;
  tools_used: string[];
  agent_proposal_from_kb?: boolean;
}

export interface AgentKbResolveResponse {
  results: AmbiguityAgentResult[];
  token_usage: TokenCount;
}

export interface AgentKbResolveSingleResponse {
  result: AmbiguityAgentResult;
  token_usage: TokenCount;
}

export interface TokenCount {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

// --- Execution Plan (Fase 2) ---

export interface ResolutionSource {
  file_name: string;
  evidence_quote: string;
  kb_item_id?: string;
  search_score?: number;
}

export interface ProposedResolution {
  ambiguity_id: string;
  proposed_text: string;
  confidence: 'high' | 'medium' | 'low';
  sources: ResolutionSource[];
  user_action?: 'approved' | 'modified' | 'rejected' | 'pending';
  user_modified_text?: string;
}

export interface StepPlan {
  step_id: string;
  name: string;
  status: 'completed' | 'pending' | 'skipped';
  skip_reason?: string;
  user_override?: 'force_include' | 'force_skip';
}

export interface ExecutionPlan {
  complexity: 'simple' | 'medium' | 'complex';
  reasoning: string;
  validation_fields_detected: number;
  estimated_requirements_count: number;
  proposed_resolutions: ProposedResolution[];
  unresolved_ambiguity_ids: string[];
  recommended_steps: StepPlan[];
  skip_reasons: Record<string, string>;
}

// --- Storia Concordata (Fase 5.5) ---

export interface TextPatch {
  patch_id: string;
  ambiguity_id: string;
  original_text: string;
  enriched_text: string;
  patch_type: 'append' | 'replace' | 'annotate';
  resolution_source: 'auto_resolve' | 'user_manual' | 'user_edit';
  confidence?: 'high' | 'medium' | 'low';
  kb_sources?: ResolutionSource[];
  turn: number;
  created_at: string;
  created_by: 'agent' | 'user';
}

export interface StoryTurn {
  turn_number: number;
  timestamp: string;
  story_snapshot: string;
  patches_applied: TextPatch[];
  patches_cumulative: TextPatch[];
  action: 'auto_resolve' | 'user_approve' | 'user_edit' | 'user_reject' | 'regenerate';
  summary: string;
}

export interface StoryHistory {
  original_story: string;
  turns: StoryTurn[];
  current_turn: number;
  concorded_story: string;
}

// Story description
export interface StoryDescriptionResponse {
  story_id: string;
  title: string;
  description: string;
  token_usage: TokenCount;
}

/**
 * Interface for the analysis result
 */
export interface AnalysisResult {
  core_analysis: AnalysisContent;
  requirements_analysis: AnalysisContent;
  ambiguities: Ambiguity[];
  token_usage: TokenCount;
}


/**
 * Interface for the structured analysis content
 */
export interface AnalysisContent {
  scenario_analysis: string;
  config_testdata_analysis: string;
}

/**
 * Interface for the structured supportive analysis content
 */
export interface SupportiveAnalysisContent {
  scenario_enrichments: string;
  config_testdata_enrichments: string;
}

// TEST DATA MANAGEMENT & PARAMETRI DI CONFIGURAZIONE

export interface ConfigParameter {
  parameter: string;
  type: string;
  allowed_values: string | string[];
  default: any;
  used_in: string[];
  impact: string;
}

export interface BaseDataset {
  field: string;
  valid_values: string[];
  invalid_values: string[];
  rules: string;
}

export interface DynamicData {
  field: string;
  generation: string;
}

export interface SensitiveData {
  type: string;
  handling: string;
}

export interface EdgeCase {
  scenario: string;
  relevance: string;
}

export interface TestDataManagement {
  base_dataset: BaseDataset[];
  dynamic_data: DynamicData[];
  sensitive_data: SensitiveData[];
  edge_cases: EdgeCase[];
}

export interface DataParametersAnalysis {
  // Flag properties to indicate presence of sections (may not exist in older responses)
  has_config_parameters?: boolean;
  has_test_data_management?: boolean;
  has_base_dataset?: boolean;
  has_dynamic_data?: boolean;
  has_sensitive_data?: boolean;
  has_edge_cases?: boolean;

  // The actual data
  configuration_parameters: ConfigParameter[];
  test_data_management: TestDataManagement;
  config_testdata_analysis?: string;
}

export interface DataParametersResponse {
  analysis_results: DataParametersAnalysis;
  token_usage: TokenCount;
}

/**
 * Represents an extracted substring with scenario potential
 */
export interface ExtractedSubstring {
  id: string;
  text: string;
  scenario_potential: string;
  scenario_categories: string[];
}

/**
 * Response model for the scenario extraction endpoint
 */
export interface ExtractionResultModel {
  extracted_substrings: ExtractedSubstring[];
  token_usage?: TokenCount;
}

/**
 * Represents the structure of the response received from the
 * /findkbmatches/ API endpoint.
 */
export interface FindKbMatchesResponse {
  /**
   * The list of ambiguities processed by the endpoint.
   * Some ambiguities may now have their `linkedKbItems` property populated
   * if the backend found potential matches.
   */
  ambiguities: Ambiguity[];

  /**
   * The aggregated token usage information for all the LLM calls
   * performed during the find-kb-matches operation on the backend.
   */
  token_usage: TokenCount;
}

// Update the response interface to include KB item mappings
export interface ProcessAmbiguitiesResponse {
  core_analysis: AnalysisContent; // The primary output
  token_usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number; };
  kb_items_created: number;
  kb_item_mappings: { [ambiguityId: string]: LinkedKnowledgeBaseItem[] };
  // Note: requirements_analysis is intentionally omitted from this response.
}

// === INTERFACES FOR TEST REFINEMENT (CONSOLIDATION) ===

// Interfaccia per un singolo test case grezzo da inviare
export interface RawTestCase {
  metadata: {
    displayId: string;
    // Potentially other metadata like microScenarioId, originalTestIndex if needed for tracing
  };
  data: { [key: string]: any }; // The actual test steps/data
}

// Interfaccia per il corpo della richiesta di raffinamento
export interface RefinementRequest {
  project: string,
  nome_sessione: string,
  user_story: string; // Contextual user story or narrative
  raw_test_cases: RawTestCase[];
  template_description: string; // The description of the desired output format
  output_template: any[]; // The template JSON that defines the structure of output
  inference_params: InferenceParams;
  language: string;
}

// Interfaccia per un singolo test case raffinato ricevuto
export interface DynamicRefinedTestCase {
  id: string;
  type: string;
  title: string;
  rationale: string;
  data: { [key: string]: any };
  sourceTestIds?: string[];
  sourceMicroScenarioIds?: string[];
  sequentialId?: number; // <-- global unified id for final testbook
}

// Interfaccia per la risposta completa di raffinamento
export interface RefinementResponse {
  tests: any[]; // This is now an array of HTML strings
  token_usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

// Interfacce per il nuovo processo di raggruppamento
export interface ConsolidationGroup {
  test_ids: string[];
  group_rationale: string;
}

export interface GroupingRequest {
  project: string;
  nome_sessione: string;
  user_story: string;
  raw_test_cases: RawTestCase[];
  inference_params: InferenceParams;
  language: string;
}

export interface GroupingResponse {
  consolidation_groups: ConsolidationGroup[];
  token_usage: TokenCount;
}
